import type { CodeTriggerT, TimelineI, TimelineLabelI, TimelineLabelTypeT } from "tweenly"
import AddRoundedIcon from "@mui/icons-material/AddRounded"
import ClearIcon from "@mui/icons-material/Clear"
import DataObjectRoundedIcon from "@mui/icons-material/DataObjectRounded"
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded"
import FirstPageIcon from "@mui/icons-material/FirstPage"
import LastPageIcon from "@mui/icons-material/LastPage"
import PauseRoundedIcon from "@mui/icons-material/PauseRounded"
import RepeatRoundedIcon from "@mui/icons-material/RepeatRounded"
import StopCircleRoundedIcon from "@mui/icons-material/StopCircleRounded"
import StopRoundedIcon from "@mui/icons-material/StopRounded"
import { Tooltip, useTheme } from "@mui/material"
import Grid from "@mui/material/Grid"
import Typography from "@mui/material/Typography"
import * as React from "react"
import { forwardRef, memo, useMemo } from "react"
import { LabelCodeTrigger } from "../../../../data/defaults/codetriggers.types.defaults"
import { createTimelineLabel } from "tweenly"
import useFramerate from "../../../../hooks/useFramerate"
import { useAppSelector } from "../../../../hooks/useRedux"
import useTimeStepsDensity from "../../../../hooks/useTimeStepsDensity"
import { useTimelineActions } from "../../../../hooks/useTimelineActions"
import { useTimelineLabels } from "../../../../hooks/useTimelineLabels"
import { useMasterTimeline } from "../../../../hooks/useMasterTimeline"
import { filterTimelineLabelsInTime } from "../../../../store/slices/timeline.slice"
import { TIMELINE_TWEEN } from "../../../../style/sizing"
import RightClickContextMenu, { MenuItemT } from "../../../common/ContextMenu/RightClickContextMenu"
import CodeTriggerEditor from "../CodeTriggerEditor"

type Props = {
    width: number
    index: number
    handleClickTimeStamp: (time: number) => void
}

const TimeStamp = memo(
    forwardRef<HTMLDivElement, Props>(({ width, index, handleClickTimeStamp }, ref) => {
        const theme = useTheme()
        const timeline: TimelineI = useAppSelector((state) => state.timeline.value)
        const { resultTimeFramerate } = useFramerate()
        const {
            addTimelineLabel,
            delTimelineLabel,
            updTimelineLabelCode,
            updateTimeline,
            setIntroEnd,
            setOutroStart,
            clearIntroEnd,
            clearOutroStart,
        } = useTimelineActions()
        const { labelsInTimeStamp } = useTimelineLabels()

        //**
        // *
        // [NOTE]: freezeTime is temporary hidden, but do not delete existing logic
        // *
        //**
        const handleSetFreezeTime = (time: number) => {
            updateTimeline("freezeTime", time)
        }
        const handleClearFreezeTime = () => {
            updateTimeline("freezeTime", null)
        }

        const [editorIsOpen, openEditor] = React.useState<boolean>(false)
        const [editorCode, setEditorCode] = React.useState<CodeTriggerT | null>(null)
        const [editorLabel, setEditorLabel] = React.useState<string | null>(null)

        const handleCloseEditor = () => {
            openEditor(false)
        }
        const handleSaveLabelCode = (newCode: CodeTriggerT) => {
            if (newCode.scope !== "label") return
            if (!editorLabel) return

            if (newCode.type === "init") {
                updTimelineLabelCode(editorLabel, newCode)
            }
        }
        const handleEditLabelCode = (label: string) => {
            const labelCode = timeline.labels.filter((lbl) => {
                return lbl.title === label
            })[0].onLabelCode

            setEditorCode(labelCode ? labelCode : LabelCodeTrigger)
            setEditorLabel(label)
            openEditor(true)
        }

        const timelinePreferences = useAppSelector((state) => state.timelinePreferences.value)
        const tweensPerSec: number = timelinePreferences.timelineTweensPerSec

        const { masterTimelineDuration: duration, effectiveTimelineDuration: effectiveDuration } =
            useMasterTimeline()
        const { timeStepsDensity } = useTimeStepsDensity()
        const timelineRowWidth: number = width
        const tweenWidth: number = duration > 0 ? timelineRowWidth / duration : timelineRowWidth // width per sec
        const timeStampWidth: number = tweenWidth / tweensPerSec - 2
        const timeStepX: number = tweenWidth / tweensPerSec

        const timeStampFromTime = index / tweensPerSec
        const timeStampToTime = (index + 1) / tweensPerSec
        const timeStampDuration = 1.0 / tweensPerSec
        const framerate = useAppSelector((state) => state.timelinePreferences.value.framerate)

        // one call per render instead of four: filterTimelineLabelsByTime walks the labels and
        // the result is then sorted, and this used to run again for the menu, the background
        // and twice for the tooltip
        const labelsOfStamp: TimelineLabelI[] = labelsInTimeStamp(
            index === 0 ? -1 : timeStampFromTime,
            timeStampToTime
        )

        const handleAddLabel = (time: number, type: TimelineLabelTypeT, jumpto?: string) => {
            addTimelineLabel(
                createTimelineLabel(
                    time,
                    type,
                    resultTimeFramerate(time).toString() + " " + type,
                    jumpto
                )
            )
        }

        const handleDeleteLabel = (title: string) => {
            delTimelineLabel(title)
        }

        const handleSeekTimeStamp = () => {
            if (timeStampToTime <= effectiveDuration) {
                handleClickTimeStamp(timeStampToTime)
            }
        }

        const timelineLabelColor = (label: TimelineLabelI): string => {
            return timeline.freezeTime === label.time
                ? theme.palette.error.light
                : label.type === "pause"
                ? theme.palette.warning.light
                : label.type === "jump"
                ? theme.palette.info.main
                : label.type === "refresh"
                ? theme.palette.success.light
                : theme.palette.action.active
        }

        const getLabelMenuItems = (label: TimelineLabelI): MenuItemT[] => {
            return label.type === "refresh"
                ? []
                : [
                      {
                          icon: <DataObjectRoundedIcon />,
                          title: "Edit " + resultTimeFramerate(label.time) + " " + label.type,
                          onClick: () => handleEditLabelCode(label.title),
                          divider: false,
                      },
                      {
                          icon: <DeleteRoundedIcon />,
                          title: "Delete " + resultTimeFramerate(label.time) + " " + label.type,
                          onClick: () => handleDeleteLabel(label.title),
                          divider: true,
                          color: "error",
                      },
                  ]
        }

        const getJumpToLabelsSubmenu = (titlePrefix: string, hidden: boolean): MenuItemT[] => {
            return timeline.labels
                ? [...timeline.labels]
                      .sort((a: TimelineLabelI, b: TimelineLabelI) => {
                          return a.time - b.time
                      })
                      .map((lbl) => {
                          return {
                              icon: <RepeatRoundedIcon />,
                              title:
                                  titlePrefix +
                                  ": Add jump to " +
                                  resultTimeFramerate(lbl.time).toString() +
                                  " label",
                              onClick: () => handleAddLabel(timeStampToTime, "jump", lbl.title),
                              hidden,
                          }
                      })
                : []
        }

        const getLabelsMenuItemsByIndex = (): MenuItemT[] => {
            const emptyLabelsAtZero =
                index === 0 && filterTimelineLabelsInTime(timeline, 0)?.length === 0
            const emptyLabelsAtTimeStamp =
                filterTimelineLabelsInTime(timeline, timeStampToTime)?.length === 0

            const canSetIntroEnd = !timeline.outroStart || timeStampToTime < timeline.outroStart
            const canSetOutroStart = !timeline.introEnd || timeStampToTime > timeline.introEnd

            const introBandVisible = !!timeline.introEnd && timeStampFromTime < timeline.introEnd
            const outroBandVisible =
                !!timeline.outroStart &&
                timeStampToTime >= timeline.outroStart &&
                timeStampFromTime < effectiveDuration

            const labelsInStamp = labelsOfStamp

            interface MenuItemWithMeta extends MenuItemT {
                _isIntroOutro?: boolean
            }

            let menuItems: MenuItemWithMeta[] = [
                {
                    icon: <FirstPageIcon />,
                    title: "Set Intro zone at " + resultTimeFramerate(timeStampToTime),
                    onClick: () => setIntroEnd(timeStampToTime),
                    divider: false,
                    hidden: timeline.introEnd !== null && timeline.introEnd !== undefined,
                    disabled: !canSetIntroEnd,
                    _isIntroOutro: true,
                },
                {
                    icon: <ClearIcon />,
                    title: "Clear Intro zone at " + resultTimeFramerate(timeline.introEnd || 0),
                    onClick: () => clearIntroEnd(),
                    divider: false,
                    hidden: !introBandVisible,
                    _isIntroOutro: true,
                    color: "error",
                },
                {
                    icon: <LastPageIcon />,
                    title: "Set Outro zone at " + resultTimeFramerate(timeStampToTime),
                    onClick: () => setOutroStart(timeStampToTime),
                    divider: false,
                    hidden: timeline.outroStart !== null && timeline.outroStart !== undefined,
                    disabled: !canSetOutroStart,
                    _isIntroOutro: true,
                },
                {
                    icon: <ClearIcon />,
                    title: "Clear Outro zone at " + resultTimeFramerate(timeline.outroStart || 0),
                    onClick: () => clearOutroStart(),
                    divider: false,
                    hidden: !outroBandVisible,
                    _isIntroOutro: true,
                    color: "error",
                },
                {
                    icon: <StopCircleRoundedIcon />,
                    title: "Clear freeze at " + resultTimeFramerate(timeStampToTime),
                    onClick: () => handleClearFreezeTime(),
                    divider: true,
                    hidden: true, //timeStampToTime !== timeline.freezeTime, //[NOTE] temporary hidden: true
                    color: "error",
                },
                {
                    icon: <StopRoundedIcon />,
                    title: "Freeze at " + resultTimeFramerate(timeStampToTime),
                    onClick: () => handleSetFreezeTime(timeStampToTime),
                    divider: true,
                    hidden: true, //timeStampToTime === timeline.freezeTime, //[NOTE] temporary hidden: true
                },
                ...labelsInStamp.flatMap((label) => getLabelMenuItems(label)),
                {
                    icon: <AddRoundedIcon />,
                    title: "0: Add continue label",
                    onClick: () => handleAddLabel(0, "continue"),
                    divider: false,
                    hidden: !emptyLabelsAtZero,
                },
                {
                    icon: <PauseRoundedIcon />,
                    title: "0: Add pause label",
                    onClick: () => handleAddLabel(0, "pause"),
                    divider: true,
                    hidden: !emptyLabelsAtZero,
                },
                ...getJumpToLabelsSubmenu("0", !emptyLabelsAtZero),
                {
                    icon: <AddRoundedIcon />,
                    title: resultTimeFramerate(timeStampToTime) + ": Add continue label",
                    onClick: () => handleAddLabel(timeStampToTime, "continue"),
                    divider: false,
                    hidden: !emptyLabelsAtTimeStamp,
                },
                {
                    icon: <PauseRoundedIcon />,
                    title: resultTimeFramerate(timeStampToTime) + ": Add pause label",
                    onClick: () => handleAddLabel(timeStampToTime, "pause"),
                    divider: true,
                    hidden: !emptyLabelsAtTimeStamp,
                },
                ...getJumpToLabelsSubmenu(
                    resultTimeFramerate(timeStampToTime).toString(),
                    !emptyLabelsAtTimeStamp
                ),
            ]

            const filteredItems = menuItems.filter((item) => !item.hidden)

            let lastIntroOutroIndex = -1
            for (let i = 0; i < filteredItems.length; i++) {
                if (filteredItems[i]._isIntroOutro) {
                    lastIntroOutroIndex = i
                }
            }

            if (lastIntroOutroIndex >= 0) {
                filteredItems[lastIntroOutroIndex].divider = true
            }

            return filteredItems.map(({ _isIntroOutro, ...item }) => item as MenuItemT)
        }

        // `[...Array(timeStepsDensity)].length` is just `timeStepsDensity` — it allocated a
        // ~500 element array three times per render only to read its length back
        const isLastTimeStamp = timeStepsDensity - index === 0
        const timeStampOuterWidth = isLastTimeStamp ? tweenWidth : timeStampWidth

        const getTimeStampBackground = (width: number): string => {
            let background: string = ""
            const timelineLabelWidth = 6

            const baseColor =
                timeStampFromTime < effectiveDuration
                    ? theme.palette.grey[400]
                    : theme.palette.grey[600]

            const labels: TimelineLabelI[] = labelsOfStamp

            if (
                timeline.freezeTime &&
                timeline.freezeTime >= timeStampFromTime &&
                timeline.freezeTime <= timeStampToTime
            ) {
                labels.push({
                    time: timeline.freezeTime,
                    type: "continue",
                    title: "",
                    onLabelCode: null,
                })
                labels.sort((a: TimelineLabelI, b: TimelineLabelI) => {
                    return a.time - b.time
                })
            }

            if (labels.length > 0) {
                background = `linear-gradient(to right, ${baseColor} 0px,`

                labels.forEach((lbl) => {
                    background += `${baseColor} ${
                        (width * (lbl.time - timeStampFromTime)) / timeStampDuration -
                        timelineLabelWidth / 2
                    }px,${timelineLabelColor(lbl)} ${
                        (width * (lbl.time - timeStampFromTime)) / timeStampDuration -
                        timelineLabelWidth / 2
                    }px,${timelineLabelColor(lbl)} ${
                        (width * (lbl.time - timeStampFromTime)) / timeStampDuration +
                        timelineLabelWidth / 2
                    }px,${baseColor} ${
                        (width * (lbl.time - timeStampFromTime)) / timeStampDuration +
                        timelineLabelWidth / 2
                    }px,`
                })

                background += `${baseColor} ${width}px)`
            } else {
                background = baseColor
            }

            return background
        }
        const displayTweenValue = useMemo(() => {
            if (timeStampWidth >= 20) {
                if (framerate === 50) {
                    return Math.round(timeStampToTime / 0.02)
                } else if (framerate === 25) {
                    return Math.round(timeStampToTime / 0.04)
                } else if (framerate === 1) {
                    return timeStampToTime
                }
            } else {
                return "."
            }
        }, [framerate, timeStampToTime, timeStampWidth])

        return (
            <Grid
                ref={ref}
                container
                key={index}
                alignItems="center"
                onClick={(e) => {
                    handleSeekTimeStamp()
                }}
                // Static, theme-aware values stay in `sx` — they hash to ONE emotion class
                // shared by every cell. Per-cell values go into `style`: `sx` turns each
                // distinct combination into a new class and inserts a new CSS rule, and
                // inserting a rule invalidates the style of the whole document. With hundreds
                // of cells, each with its own `left`, `width` and `linear-gradient`, that is
                // where `Recalculate Style` (60% of the profile) came from.
                sx={{
                    position: "absolute",
                    borderRadius: TIMELINE_TWEEN.borderRadius,
                }}
                style={{
                    left: index * timeStepX + 2 + "px",
                    background: getTimeStampBackground(timeStampOuterWidth),
                    width: timeStampOuterWidth + "px",
                    cursor: timeStampToTime <= effectiveDuration ? "pointer" : "",
                }}
            >
                {timeStampFromTime >= effectiveDuration ? (
                    <Grid
                        item
                        sx={{ position: "relative", textAlign: "center", height: "100%" }}
                        style={{ width: timeStampOuterWidth + "px" }}
                    >
                        <Typography variant="caption" color={theme.palette.text.primary}>
                            {displayTweenValue}
                        </Typography>
                    </Grid>
                ) : (
                    <RightClickContextMenu
                        menuLabel={
                            <Tooltip
                                title={
                                    (labelsOfStamp.length > 1 ? "labels " : "") +
                                    labelsOfStamp.map((lbl) => {
                                        return lbl.type === "refresh"
                                            ? lbl.type + " " + lbl.title
                                            : lbl.title +
                                                  (lbl.jumpto !== undefined && lbl.jumpto !== ""
                                                      ? " to " + lbl.jumpto
                                                      : "") +
                                                  ","
                                    })
                                }
                            >
                                <Grid
                                    item
                                    sx={{
                                        position: "relative",
                                        textAlign: "center",
                                        height: "100%",
                                    }}
                                    style={{ width: timeStampOuterWidth + "px" }}
                                >
                                    <Typography
                                        variant="caption"
                                        color={theme.palette.text.primary}
                                    >
                                        {displayTweenValue}
                                    </Typography>
                                </Grid>
                            </Tooltip>
                        }
                        // passed as a function: the ~12 items each hold an MUI SVG icon and
                        // are only needed once the menu is actually open
                        data={getLabelsMenuItemsByIndex}
                    />
                )}
                {/* mounted on demand — a closed MUI Dialog per time stamp is still a component
                    tree per cell, and there are hundreds of cells */}
                {editorIsOpen && (
                    <CodeTriggerEditor
                        open={editorIsOpen}
                        title="Code to be uploaded into the HTML/JS engine"
                        code={editorCode}
                        onSaveHandler={handleSaveLabelCode}
                        onCloseHandler={handleCloseEditor}
                    />
                )}
            </Grid>
        )
    })
)

export default TimeStamp
